/** @jsx jsx */
import { jsx } from '@emotion/react';
import { MouseEvent as ReactMouseEvent } from 'react';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

import { SIDEBAR_ID } from '../constants';
import {
  UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
  IN_APP_UNIVERSAL_SIDEBAR_WIDTH,
} from '../../../constants';
import { CreateDropdown } from './CreateDropdown';
import { Divider } from '../Divider';
import Footer from './Footer';
import InAppLinks from './InAppLinks';
import InAppSidebarSubheaderButton from './InAppSidebarSubheaderButton';
import { useQuerySidebar } from './useQuerySidebar';
import { useUniversalNavContext } from '../../../hooks';

const Styles = labelStyles('InAppSidebar', {
  root: ({
    isCollapsed,
    isFullWidth,
    isCreateMenuOpen,
  }: Pick<
    InAppSidebarProps,
    'isCollapsed' | 'isFullWidth' | 'isCreateMenuOpen'
  >) => [
    {
      position: 'relative',
      // Create grid to stretch content so that it scrolls.
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',
      padding: 'var(--zds-space-16, 16px) 0 var(--zds-space-8, 8px)',
      height: 'inherit',
      // Noticed some jankiness with `transition` and `animation`.
      // This appears to improve it at least a bit.
      willChange: 'transform, width',
      // Only set the `overflowY` to `auto` when the 'Create' dropdown is open.
      // Setting overflowY causes overflowX to also be set to 'auto', per the CSS spec: https://www.w3.org/TR/css-overflow-3/#valdef-overflow-auto
      // This causes the X overflow to be hidden, which hides the Menu of the Create dropdown
      overflowY: isCreateMenuOpen ? 'unset' : 'auto',
      scrollBehavior: 'smooth',
      // Hide scrollbar for WebKit browsers
      '::-webkit-scrollbar': {
        width: '0px',
        background: 'transparent',
      },
      // Hide scrollbar for Firefox
      scrollbarWidth: 'none',
      justifyContent: 'space-between',
    },

    !isFullWidth &&
      withModifier('constrainedWidth', {
        width: IN_APP_UNIVERSAL_SIDEBAR_WIDTH,
        maxWidth: '100vw',
      }),
    isCollapsed &&
      withModifier('collapsed', {
        width: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
        overflow: 'visible',
        align: 'center',
      }),
  ],
  rootWrapper: ({ isShadowed }: Pick<InAppSidebarProps, 'isShadowed'>) => [
    { height: 'inherit', position: 'relative' },
    isShadowed &&
      withModifier('shadowed', {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '100%',
          bottom: 0,
          width: 10,
          pointerEvents: 'none',
          // Uses negative zIndex to make sure this is behind
          // FloatingBox of the Create dropdown
          zIndex: -1,
          background: `linear-gradient(
          90deg,
          rgba(0, 0, 0, 0.10) 0%,
          rgba(0, 0, 0, 0.03) 50%,
          rgba(0, 0, 0, 0.00) 100%
        )`,
        },
      }),
  ],

  header: {
    flex: 'none',
    padding: '0 var(--zds-space-8, 8px)',
    marginBottom: 'var(--zds-space-12, 12px)',
  },

  content: ({ isCollapsed }: Pick<InAppSidebarProps, 'isCollapsed'>) => [
    {
      flexBasis: 'auto',
      flexShrink: 0,
      flexGrow: 0,

      // Hide scrollbar, especially on Windows and in Firefox on macOS.
      // Shouldn't be a usability issue since a) scrolling is unlikely
      // to be necessary for most screens, and b) scrolling should still
      // be obvious due to clipped content.
      scrollbarWidth: 'none',
      '::-webkit-scrollbar': {
        display: 'none',
      },
    },
    isCollapsed &&
      withModifier('collapsed', {
        overflow: 'visible',
      }),
  ],
  footer: {
    flex: 'none',
    padding: '0 var(--zds-space-8, 8px)',
    width: '100%',
  },
});

export type InAppSidebarProps = {
  /**
   * The `id` of the account the user is currently switched to.
   */
  currentAccountId: number;
  /**
   * Indicates whether the navigation is collapsed down to icons.
   */
  isCollapsed: boolean;
  /**
   * Whether the 'Create' dropdown is open.
   */
  isCreateMenuOpen?: boolean;
  /**
   * Whether the sidebar is as wide as it can be.
   * When `true`, will render as wide as it can.
   * When `false`, will constrain to a `max-width`.
   */
  isFullWidth?: boolean;
  /**
   * Whether the sidebar has a shadow. Should only be used when
   * the sidebar is floating.
   */
  isShadowed?: boolean;
  /**
   * Optional handler for when anywhere on the nav is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
  /**
   * Optional handler for when the `nav` is hovered.
   */
  onMouseEnter?: (e: ReactMouseEvent) => void;
  /**
   Optional handler for when the `nav` is unhovered.
   */
  onMouseLeave?: (e: ReactMouseEvent) => void;
  /**
   * The `href` of the currently selected link within `links`.
   */
  selectedHref: string;
};

/**
 * @private
 * Sidebar to render when a user is on an in-app page. Includes:
 * - 'Create' action ('Create Zap' button or 'Create' dropdown)
 * - Product links
 * - Account usage
 * - Terms of Service updates (if any)
 * - Call to Action (if any)
 */
const InAppSidebar = ({
  currentAccountId,
  isCollapsed = false,
  isFullWidth = true,
  isShadowed,
  onClick,
  onMouseEnter,
  onMouseLeave,
  selectedHref = '',
}: InAppSidebarProps) => {
  const { data: sideNavData } = useQuerySidebar();
  const { isCreateMenuOpen } = useUniversalNavContext();

  const { getFullyQualifiedURL } = useUniversalNavContext();

  const mouseProps = {
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
  };

  const CreateAction = sideNavData.createOptions ? (
    <CreateDropdown
      isCollapsed={isCollapsed}
      items={sideNavData.createOptions}
    />
  ) : (
    <InAppSidebarSubheaderButton
      href={
        getFullyQualifiedURL(
          '/webintent/create-zap?entry-point-method=make_a_zap_call_to_action'
        ) as string
      }
      iconName="formAdd"
      isCollapsed={isCollapsed}
      text="Create Zap"
    />
  );

  return (
    <div data-zds css={Styles.rootWrapper({ isShadowed })}>
      <nav
        aria-label="Site Links"
        css={Styles.root({
          isCollapsed,
          isFullWidth,
          isCreateMenuOpen,
        })}
        data-testid="universalSidebar"
        id={SIDEBAR_ID}
        {...mouseProps}
        data-zds
      >
        <div data-zds>
          <div css={Styles.header} data-zds>
            {CreateAction}
          </div>
          <div css={Styles.content({ isCollapsed })} data-zds>
            <InAppLinks
              isCollapsed={isCollapsed}
              links={sideNavData.itemGroups}
              moreLinks={sideNavData.moreLinks}
              selectedHref={selectedHref}
            />
          </div>
        </div>

        <div css={Styles.footer} role="region" data-zds>
          {!isCollapsed && <Divider />}
          <Footer
            currentAccountId={currentAccountId}
            isCollapsed={isCollapsed}
            selectedHref={selectedHref}
          />
        </div>
      </nav>
    </div>
  );
};

export default InAppSidebar;
